import React, { useState, useEffect } from 'react';
import { FolderTree, Tag, MapPin, RefreshCw, Globe, FileText, Tent, PartyPopper, Mail, Map, Clock, Save, Search, Copy, ExternalLink, X, Zap } from 'lucide-react';

const API_URL = window.location.origin;

export const AdminOptionsTab = ({
  categories, tags, markets, expos, festivals,
  setShowCategoriesModal, setShowTagsModal, setShowMarketsModal,
  setShowExposModal, setShowFestivalsModal,
  setMessage,
  fetchCategoriesAndTags, fetchStalls, fetchVendors,
  fetchLegalContent, setShowLegalModal,
  setShowEmailDashboard,
  setShowProvincesModal,
}) => {
  // Payment settings — grace period before a first-time failed payment
  // triggers an automatic account suspension + stall draft.
  const [graceDays, setGraceDays] = useState(3);
  const [graceSaving, setGraceSaving] = useState(false);
  const [graceLoaded, setGraceLoaded] = useState(false);

  // Search Engine Tools modal
  const [seoModalOpen, setSeoModalOpen] = useState(false);
  const [indexNowLoading, setIndexNowLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const SITEMAP_URL = 'https://wayo.co.za/api/sitemap.xml';

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/admin/settings/payment`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && typeof d.grace_period_days === 'number') setGraceDays(d.grace_period_days); setGraceLoaded(true); })
      .catch(() => setGraceLoaded(true));
  }, []);

  const saveGrace = async () => {
    setGraceSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/settings/payment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ grace_period_days: Number(graceDays) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      setMessage({ type: 'success', text: `Payment grace period updated to ${data.grace_period_days} day${data.grace_period_days === 1 ? '' : 's'}` });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Failed to save' });
    } finally {
      setGraceSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Categories Card */}
      <div className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-[#FFD12D] rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group" onClick={() => setShowCategoriesModal(true)}>
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-[#FFD12D]/20 flex items-center justify-center group-hover:bg-[#FFD12D] transition-colors">
            <FolderTree className="w-6 h-6 text-[#FFD12D] group-hover:text-[#252525]" />
          </div>
          <span className="text-2xl font-bold text-[#252525] dark:text-white">{categories.length}</span>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Categories</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage stall categories</p>
      </div>

      {/* Tags Card */}
      <div className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-[#FFD12D] rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group" onClick={() => setShowTagsModal(true)}>
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center group-hover:bg-purple-500 transition-colors">
            <Tag className="w-6 h-6 text-purple-600 group-hover:text-white" />
          </div>
          <span className="text-2xl font-bold text-[#252525] dark:text-white">{tags.length}</span>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Tags</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage stall tags</p>
      </div>

      {/* Provinces Card (new) */}
      <div
        className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-[#FFD12D] rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group"
        onClick={() => setShowProvincesModal?.(true)}
        data-testid="admin-options-provinces-card"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center group-hover:bg-teal-500 transition-colors">
            <Map className="w-6 h-6 text-teal-600 group-hover:text-white" />
          </div>
          <span className="text-2xl font-bold text-[#252525] dark:text-white">9</span>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Provinces</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Assign markets, expos & festivals</p>
      </div>

      {/* Markets Card */}
      <div className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-[#FFD12D] rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group" onClick={() => setShowMarketsModal(true)}>
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center group-hover:bg-blue-500 transition-colors">
            <MapPin className="w-6 h-6 text-blue-600 group-hover:text-white" />
          </div>
          <span className="text-2xl font-bold text-[#252525] dark:text-white">{markets.length}</span>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Markets</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage market locations</p>
      </div>

      {/* Expos Card */}
      <div className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-[#FFD12D] rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group" onClick={() => setShowExposModal(true)}>
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center group-hover:bg-purple-500 transition-colors">
            <Tent className="w-6 h-6 text-purple-600 group-hover:text-white" />
          </div>
          <span className="text-2xl font-bold text-[#252525] dark:text-white">{expos?.length || 0}</span>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Expos</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage expo events</p>
      </div>

      {/* Festivals Card */}
      <div className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-[#FFD12D] rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group" onClick={() => setShowFestivalsModal(true)}>
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center group-hover:bg-orange-500 transition-colors">
            <PartyPopper className="w-6 h-6 text-orange-600 group-hover:text-white" />
          </div>
          <span className="text-2xl font-bold text-[#252525] dark:text-white">{festivals?.length || 0}</span>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Festivals</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage festival events</p>
      </div>

      {/* Clear Cache Card */}
      <div className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-red-500 rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group"
        onClick={async () => {
          if (window.confirm('Clear all cached data? This will refresh categories, tags, markets, and stalls from the server.')) {
            localStorage.removeItem('wayo_categories');
            localStorage.removeItem('wayo_tags');
            localStorage.removeItem('wayo_markets');
            localStorage.removeItem('wayo_stalls');
            setMessage({ type: 'success', text: 'Cache cleared! Refreshing data...' });
            await fetchCategoriesAndTags();
            await fetchStalls();
            await fetchVendors();
          }
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center group-hover:bg-red-500 transition-colors">
            <RefreshCw className="w-6 h-6 text-red-600 group-hover:text-white" />
          </div>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Clear Cache</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Refresh all data from server</p>
      </div>

      {/* Submit Sitemap Card */}
      <div className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group"
        data-testid="submit-sitemap-btn"
        onClick={async () => {
          try {
            setMessage({ type: 'info', text: 'Submitting sitemap to search engines...' });
            const token = localStorage.getItem('token');
            const resp = await fetch(`${API_URL}/api/sitemap/submit`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await resp.json();
            if (resp.ok) {
              const r = data.results || {};
              const parts = Object.entries(r).map(([engine, info]) =>
                `${engine}: ${info.success ? 'OK' : 'Failed'}`
              ).join(', ');
              setMessage({ type: 'success', text: `Sitemap submitted! ${parts}` });
            } else {
              setMessage({ type: 'error', text: data.detail || 'Submission failed' });
            }
          } catch (err) {
            setMessage({ type: 'error', text: 'Network error submitting sitemap' });
          }
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center group-hover:bg-blue-500 transition-colors">
            <Globe className="w-6 h-6 text-blue-600 group-hover:text-white" />
          </div>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Submit Sitemap</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Notify search engines of updates</p>
      </div>

      {/* Search Engine Tools Card — one-click access to Search Console,
          Bing Webmaster, Facebook OG debugger, and the "push everything to
          IndexNow" trigger. */}
      <div
        className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-purple-500 rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group"
        onClick={() => setSeoModalOpen(true)}
        data-testid="seo-tools-btn"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center group-hover:bg-purple-500 transition-colors">
            <Search className="w-6 h-6 text-purple-600 group-hover:text-white" />
          </div>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Search Engine Tools</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Console links, OG cache refresh & IndexNow</p>
      </div>

      {/* Email Management Card */}
      <div
        className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-[#FFD12D] rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group"
        onClick={() => setShowEmailDashboard?.(true)}
        data-testid="admin-options-email-card"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-[#FFD12D]/20 flex items-center justify-center group-hover:bg-[#FFD12D] transition-colors">
            <Mail className="w-6 h-6 text-[#FFD12D] group-hover:text-[#252525]" />
          </div>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Emails</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Log, resend, ad-hoc & preferences</p>
      </div>

      {/* Legal Content Card */}
      <div className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 hover:border-[#FFD12D] rounded-xl p-5 cursor-pointer transition-all duration-300 hover:shadow-lg group"
        onClick={() => { fetchLegalContent(); setShowLegalModal(true); }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
            <FileText className="w-6 h-6 text-emerald-600 group-hover:text-white" />
          </div>
          <span className="text-2xl font-bold text-[#252525] dark:text-white">3</span>
        </div>
        <h3 className="font-bold text-[#252525] dark:text-white mb-1">Legal</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Edit terms, privacy & other</p>
      </div>
    </div>

    {/* Payment Settings — Grace Period */}
    <div className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-200 dark:border-gray-700 rounded-xl p-5" data-testid="payment-grace-settings">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
          <Clock className="w-6 h-6 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-[#252525] dark:text-white mb-1">Payment Grace Period</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            When a vendor&apos;s first monthly renewal fails on PayFast, wait this many days before auto-drafting their stall and suspending the account. Gives vendors a window to retry payment without penalty. Set to <strong>0</strong> for immediate suspension.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Grace period (days)</label>
              <input
                type="number"
                min={0}
                max={14}
                value={graceDays}
                disabled={!graceLoaded}
                onChange={(e) => setGraceDays(e.target.value)}
                className="w-32 px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-[#252525] text-[#252525] dark:text-white"
                data-testid="grace-period-input"
              />
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">0-14 days</p>
            </div>
            <button
              onClick={saveGrace}
              disabled={graceSaving || !graceLoaded}
              className="px-4 py-2 rounded-full bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] font-semibold text-sm disabled:opacity-50 flex items-center gap-2"
              data-testid="grace-period-save"
            >
              <Save className="w-4 h-4" />
              {graceSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Search Engine Tools Modal — external service quick-links + IndexNow */}
    {seoModalOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        onClick={() => setSeoModalOpen(false)}
        data-testid="seo-tools-modal"
      >
        <div
          className="bg-white dark:bg-[#1a1a1a] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b-2 border-gray-100 dark:border-gray-800 bg-white dark:bg-[#1a1a1a]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Search className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="font-bold text-lg text-[#252525] dark:text-white">Search Engine Tools</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Get WAYO indexed & refresh social previews</p>
              </div>
            </div>
            <button
              onClick={() => setSeoModalOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center"
              aria-label="Close"
              data-testid="seo-tools-close"
            >
              <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Sitemap URL */}
            <div>
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Sitemap URL</p>
              <div className="flex items-center gap-2 p-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#252525]">
                <code className="flex-1 text-sm text-[#252525] dark:text-white overflow-x-auto whitespace-nowrap font-mono">{SITEMAP_URL}</code>
                <button
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(SITEMAP_URL);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1600);
                    } catch (_) { /* clipboard blocked */ }
                  }}
                  className="px-3 py-1.5 rounded-full bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] text-xs font-bold flex items-center gap-1 flex-shrink-0"
                  data-testid="copy-sitemap-url"
                >
                  <Copy className="w-3 h-3" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">Paste this into Google Search Console → Sitemaps</p>
            </div>

            {/* Push all URLs to IndexNow */}
            <div className="rounded-xl border-2 border-purple-200 dark:border-purple-900/40 bg-purple-50 dark:bg-purple-900/10 p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#252525] dark:text-white mb-0.5">Push every stall to IndexNow</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">One-shot push to Bing, Yandex, DuckDuckGo &amp; every IndexNow partner. Use after bulk edits.</p>
                  <button
                    onClick={async () => {
                      setIndexNowLoading(true);
                      try {
                        const token = localStorage.getItem('token');
                        const resp = await fetch(`${API_URL}/api/indexnow/submit-all`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` },
                        });
                        const data = await resp.json();
                        if (resp.ok && data.success) {
                          setMessage({ type: 'success', text: `Pushed ${data.urls_submitted || data.stalls_count || 0} URLs to IndexNow` });
                        } else {
                          setMessage({ type: 'error', text: data.message || 'IndexNow push failed — the key file may still be propagating on prod. Retry in a minute.' });
                        }
                      } catch (_) {
                        setMessage({ type: 'error', text: 'Network error pushing to IndexNow' });
                      }
                      setIndexNowLoading(false);
                      setSeoModalOpen(false);
                    }}
                    disabled={indexNowLoading}
                    className="px-4 py-2 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                    data-testid="indexnow-push-btn"
                  >
                    <Zap className="w-4 h-4" />
                    {indexNowLoading ? 'Pushing…' : 'Push now'}
                  </button>
                </div>
              </div>
            </div>

            {/* External services grid */}
            <div>
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Search consoles</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { label: 'Google Search Console', href: 'https://search.google.com/search-console?resource_id=https%3A%2F%2Fwayo.co.za%2F', hint: 'Submit sitemap · Request indexing' },
                  { label: 'Bing Webmaster Tools', href: 'https://www.bing.com/webmasters/home', hint: 'Submit sitemap · URL inspection' },
                ].map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                    className="flex items-start gap-3 p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 transition-all"
                    data-testid={`seo-link-${s.label.toLowerCase().replace(/\s+/g,'-')}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-[#252525] dark:text-white truncate">{s.label}</p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{s.hint}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>

            {/* OG cache refresh */}
            <div>
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Refresh social share previews</p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
                Paste <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[10px] font-mono">https://wayo.co.za/</code> into each tool and click their &ldquo;Scrape Again&rdquo; / &ldquo;Inspect&rdquo; button so they pick up the new title, description &amp; logo.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { label: 'Facebook / WhatsApp', href: 'https://developers.facebook.com/tools/debug/?q=https%3A%2F%2Fwayo.co.za%2F' },
                  { label: 'Twitter / X Cards', href: 'https://cards-dev.twitter.com/validator' },
                  { label: 'LinkedIn Post Inspector', href: 'https://www.linkedin.com/post-inspector/inspect/https%3A%2F%2Fwayo.co.za%2F' },
                  { label: 'Pinterest Rich Pins', href: 'https://developers.pinterest.com/tools/url-debugger/?link=https%3A%2F%2Fwayo.co.za%2F' },
                ].map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-[#FFD12D] transition-all"
                    data-testid={`og-link-${s.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                  >
                    <p className="font-semibold text-sm text-[#252525] dark:text-white truncate">{s.label}</p>
                    <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};
